import moment from 'moment';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import {isInStore, hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import * as DocumentEditorLockReduxActions from 'notebook/realtime/DocumentEditorLockReduxActions';
import {DocumentEditorLock} from 'notebook/realtime/reduceDocumentEditorLockState';
import {isCurrentClient} from 'realtime/util/RealtimeUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import APIUtil from 'util/APIUtil';

export const RELEASE_LOCK_TOMBSTONE = -1;
export const releaseLockTimeoutMap: Record<string, NodeJS.Timeout | typeof RELEASE_LOCK_TOMBSTONE> = {};

const DocumentEditorLockUtil = {
  /**
   * Schedules a timeout for releasing the locks for a given set of related entry/documents corresponding to the API IDs provided.
   *
   * @param documentIds -- the API identifiers of the Entry/Documents we are trying to release
   */
  releaseLocks(documentIds: Array<string>): void {
    if (BenchlingConfig.ELN_RELEASE_DOCUMENT_LOCK_INTERVAL <= -1) {
      return;
    }

    if (
      documentIds.some(
        (documentId) =>
          releaseLockTimeoutMap[documentId] === RELEASE_LOCK_TOMBSTONE ||
          (isInStore(documentId) && hasUnsavedChanges(documentId))
      )
    ) {
      return;
    }

    for (const documentId of documentIds) {
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
      releaseLockTimeoutMap[documentId] = RELEASE_LOCK_TOMBSTONE;
    }

    const timeout = setTimeout(() => {
      for (const documentId of documentIds) {
        // If any related document has unsaved changes don't release the locks
        if (isInStore(documentId) && hasUnsavedChanges(documentId)) {
          return;
        }
      }
      if (documentIds.length > 0) {
        getStore().dispatch(DocumentEditorLockReduxActions.releaseLocks(documentIds));
      }
    }, BenchlingConfig.ELN_RELEASE_DOCUMENT_LOCK_INTERVAL);

    for (const documentId of documentIds) {
      releaseLockTimeoutMap[documentId] = timeout;
    }
  },

  clearReleaseLockTimeout(documentId: string): void {
    if (releaseLockTimeoutMap[documentId]) {
      // If the value exists and it is not a tombstone value, its a timeout so clear it
      if (releaseLockTimeoutMap[documentId] !== RELEASE_LOCK_TOMBSTONE) {
        clearTimeout(releaseLockTimeoutMap[documentId]);
      }
      delete releaseLockTimeoutMap[documentId];
    }
  },

  forceAcquireLocks(documentId: string): void {
    const documentsToLock = getDocumentsToLock(documentId);
    for (const documentId of documentsToLock) {
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
      releaseLockTimeoutMap[documentId] = RELEASE_LOCK_TOMBSTONE;
    }

    if (documentsToLock.length > 0) {
      getStore().dispatch(
        DocumentEditorLockReduxActions.acquireLocks(documentsToLock, true /* forceNetwork */)
      );
    }
  },

  acquireLocks(documentId: string): void {
    const documentsToLock = getDocumentsToLock(documentId);
    for (const documentId of documentsToLock) {
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
    }

    if (documentsToLock.length > 0) {
      getStore().dispatch(DocumentEditorLockReduxActions.acquireLocks(documentsToLock));
    }
  },

  getLock(documentId: string): DocumentEditorLock | undefined {
    return isInStore(documentId) ? getStore().getState().ui.documentEditorLockState[documentId] : undefined;
  },

  isLockedInRealtime(documentEditorLock?: DocumentEditorLock): boolean {
    if (documentEditorLock && !isCurrentClient(documentEditorLock.clientId)) {
      if (moment().isBefore(moment(documentEditorLock.expiresAt))) {
        return true;
      }
    }
    return false;
  },

  mapDocumentEditorLockStateToProps(
    state: BenchlingState,
    fileId: string
  ): {documentEditorLock?: DocumentEditorLock} {
    const documentEditorLock = state.ui.documentEditorLockState[fileId];
    if (documentEditorLock) {
      return {
        documentEditorLock,
      };
    }
    return {};
  },
};

/**
 * Returns all api identifiers of objects that should be locked/unlocked alongside the given documentlike object.
 * For example, worksheet step documents should be locked and unlocked together.
 *
 * @param documentId - the API identifier of the Entry/Document to be locked/unlocked
 * @returns an array of API identifiers to lock/unlock
 */
function getDocumentsToLock(documentId: string): Array<string> {
  const documentsToLock: Array<string> = [documentId];
  if (
    APIUtil.apiIdToType(documentId) === ModelTypeConstants.DOCUMENT &&
    BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
  ) {
    const relatedDocumentIds = DocumentStore.getRelatedDocumentIds(documentId);
    documentsToLock.push(...relatedDocumentIds.filter((id) => id !== documentId));
  }
  return documentsToLock;
}

export default DocumentEditorLockUtil;
